import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReviewerNavbar from '../components/ReviewerNavbar';
import { useReviewerAuth, ReviewerAPI } from '../context/ReviewerAuthContext';
import { useToast } from '../context/ToastContext';
import LoadingState from '../components/LoadingState';
import {
  Clock, CheckCircle, Film, Star, ChevronRight,
  AlertCircle, Inbox, RefreshCw, Circle
} from 'lucide-react';

function timeAgo(date) {
  const diff = Date.now() - new Date(date);
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function AssignmentCard({ assignment, onClick }) {
  const isCompleted = assignment.assignment_status === 'completed';
  const isInProgress = assignment.assignment_status === 'in_progress';

  return (
    <div className="card fade-up" onClick={onClick}
      style={{ padding: 22, marginBottom: 12, cursor: 'pointer', transition: 'all 0.2s', borderColor: isCompleted ? 'rgba(0,200,150,0.25)' : 'var(--border)' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--pink)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = isCompleted ? 'rgba(0,200,150,0.25)' : 'var(--border)'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', letterSpacing: '-0.01em' }}>
              {assignment.title}
            </h3>
            {isCompleted && <span className="badge badge-green">Reviewed</span>}
            {isInProgress && <span className="badge badge-amber">In Progress</span>}
            {assignment.assignment_status === 'pending' && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>New</span>}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 8 }}>
            {[assignment.genre, assignment.language, assignment.duration].filter(Boolean).join(' · ')}
          </p>
          {assignment.synopsis && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, fontWeight: 400, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {assignment.synopsis}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          {isCompleted && assignment.score_overall && (
            <div style={{ fontWeight: 900, fontSize: 20, color: 'var(--pink)' }}>★ {parseFloat(assignment.score_overall).toFixed(1)}</div>
          )}
          {assignment.recommendation && (
            <span className={`badge ${assignment.recommendation === 'recommend' ? 'badge-green' : assignment.recommendation === 'consider' ? 'badge-amber' : 'badge-red'}`}>
              {assignment.recommendation === 'recommend' ? '✓ Recommend' : assignment.recommendation === 'consider' ? '~ Consider' : '✕ Pass'}
            </span>
          )}
          <ChevronRight size={16} color="var(--text-muted)" />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1.5px solid var(--border)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Assigned {timeAgo(assignment.assigned_at)}</span>
        {assignment.deadline && (
          <span style={{ fontSize: 11, fontWeight: 600, color: new Date(assignment.deadline) < new Date() ? 'var(--red)' : 'var(--text-muted)' }}>
            Due {new Date(assignment.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        )}
      </div>
    </div>
  );
}

function PoolCard({ film, onClaim, claiming }) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{film.title}</h4>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>
            {[film.genre, film.language, film.duration].filter(Boolean).join(' · ')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {[...Array(parseInt(film.reviewer_count) || 0)].map((_, i) => (
              <div key={i} style={{ width: 14, height: 14, borderRadius: '50%', background: parseInt(film.reviewer_count) >= 2 ? 'var(--pink)' : 'var(--amber)', border: '2px solid var(--border)' }} />
            ))}
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
              {parseInt(film.reviewer_count) || 0} reviewer{parseInt(film.reviewer_count) === 1 ? '' : 's'} so far
            </span>
          </div>
        </div>
        <button onClick={() => onClaim(film.id)} disabled={claiming === film.id} className="btn-primary"
          style={{ padding: '7px 14px', fontSize: 11, flexShrink: 0 }}>
          {claiming === film.id ? 'Claiming...' : 'Claim'}
        </button>
      </div>
    </div>
  );
}

export default function ReviewerDashboard() {
  const { reviewer } = useReviewerAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [pool, setPool] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPool, setShowPool] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      // Uses ReviewerAPI (separate instance with reviewer token)
      const [dashRes, poolRes] = await Promise.all([
        ReviewerAPI.get('/api/reviewer/dashboard'),
        ReviewerAPI.get('/api/reviewer/pool'),
      ]);
      setAssignments(dashRes.data.assignments);
      setStats(dashRes.data.stats);
      setPool(poolRes.data.pool);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleClaim = async (submissionId) => {
    setClaiming(submissionId);
    try {
      await ReviewerAPI.post(`/api/reviewer/pool/${submissionId}/claim`);
      await load();
      setShowPool(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not claim film', 'error');
    } finally { setClaiming(null); }
  };

  const filtered = assignments
    .filter(a => filter === 'all' || a.assignment_status === filter)
    .filter(a => !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.genre?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <ReviewerNavbar />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontWeight: 900, fontSize: 28, color: 'var(--text)', marginBottom: 4, letterSpacing: '-0.02em' }}>
              Hey, {reviewer?.name?.split(' ')[0]} 👋
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
              {reviewer?.specialty ? `Specialist: ${reviewer.specialty}` : 'Film Reviewer · Shortflix Editorial'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <RefreshCw size={14} color="var(--text-secondary)" />
            </button>
            <button onClick={() => setShowPool(s => !s)} className="btn-primary"
              style={{ fontSize: 11, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Inbox size={13} /> Film Pool {pool.length > 0 && `(${pool.length})`}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="reviewer-stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label:'Assigned', val:stats.total||0, color:'var(--pink)', icon:<Film size={14}/>, f:'all' },
            { label:'Pending', val:stats.pending||0, color:'var(--amber)', icon:<Clock size={14}/>, f:'pending' },
            { label:'In Progress', val:stats.in_progress||0, color:'var(--blue)', icon:<Star size={14}/>, f:'in_progress' },
            { label:'Completed', val:stats.completed||0, color:'var(--green)', icon:<CheckCircle size={14}/>, f:'completed' },
          ].map(({ label, val, color, icon, f }) => (
            <div key={label} className="card" onClick={() => setFilter(f)}
              style={{ padding: '14px 16px', cursor: 'pointer', borderColor: filter === f ? 'var(--pink)' : 'var(--border)', transition: 'all 0.2s' }}>
              <div style={{ color, marginBottom: 6 }}>{icon}</div>
              <div style={{ fontWeight: 900, fontSize: 24, color, marginBottom: 2 }}>{val}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Blind review notice */}
        <div style={{ background: 'rgba(59,130,246,0.06)', border: '1.5px solid rgba(59,130,246,0.2)', borderRadius: 'var(--r-md)', padding: '11px 16px', marginBottom: 18, display: 'flex', gap: 10 }}>
          <AlertCircle size={14} color="var(--blue)" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500, lineHeight: 1.5 }}>
            <strong>Blind review active.</strong> You see only film details — never filmmaker names, emails, or phone numbers.
          </p>
        </div>

        {/* Film Pool */}
        {showPool && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontWeight: 800, fontSize: 17, color: 'var(--text)' }}>Available Films ({pool.length})</h2>
              <button onClick={() => setShowPool(false)} style={{ background: 'none', color: 'var(--text-muted)', border: 'none', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: 600 }}>Hide</button>
            </div>
            {pool.length === 0 ? (
              <div className="card" style={{ padding: 24, textAlign: 'center' }}>
                <CheckCircle size={24} color="var(--green)" style={{ margin: '0 auto 8px' }} />
                <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>No films available to claim right now.</p>
              </div>
            ) : pool.map(film => <PoolCard key={film.id} film={film} onClaim={handleClaim} claiming={claiming} />)}
          </div>
        )}

        {/* Search + Filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search assigned films..."
            style={{ flex: 1, minWidth: 200, fontSize: 13, padding: '8px 12px' }}
          />
          {[['all','All'],['pending','Pending'],['in_progress','In Progress'],['completed','Done']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: filter === val ? 'var(--pink)' : 'var(--bg-elevated)', color: filter === val ? '#fff' : 'var(--text-secondary)', border: `1.5px solid ${filter === val ? 'var(--pink)' : 'var(--border)'}`, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <LoadingState label="Loading films…" minHeight={160} />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Film size={32} color="var(--text-muted)" style={{ margin: '0 auto 12px' }} />
            <h3 style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', marginBottom: 6 }}>
              {search ? `No films matching "${search}"` : filter === 'all' ? 'No films assigned yet' : `No ${filter.replace('_',' ')} films`}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 400 }}>
              {filter === 'all' && !search ? 'The admin team will assign films to you, or pick from the film pool above.' : 'Try a different filter.'}
            </p>
          </div>
        ) : filtered.map(a => (
          <AssignmentCard key={a.assignment_id} assignment={a}
            onClick={() => navigate(`/reviewer/review/${a.assignment_id}`)} />
        ))}
      </div>
    </div>
  );
}
